import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IconType } from 'react-icons';

import { useDisclosure } from '@chakra-ui/react';
import {
  KeyboardEventType,
  KeyboardKey,
  WindowEventType,
} from '@this+that/utils';
import { LuPlus } from 'react-icons/lu';

import { useClickOutside } from '../../../../../hooks/useClickOutside';

const MENU_VERTICAL_GAP = 4;

export interface ComposeMenuItem {
  id: string;
  labelKey: string;
  icon: IconType;
  onClick: () => void;
}

export interface MenuPosition {
  top: number;
  left: number;
}

export interface UseComposeDropdownMenuReturn {
  menuRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  portalMenuRef: RefObject<HTMLDivElement | null>;
  isMenuOpen: boolean;
  menuPosition: MenuPosition | null;
  onToggleMenu: () => void;
  menuItems: ComposeMenuItem[];
}

export function useComposeDropdownMenu(
  onCreateTask: () => void,
): UseComposeDropdownMenuReturn {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const portalMenuRef = useRef<HTMLDivElement | null>(null);

  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const {
    isOpen: isMenuOpen,
    onToggle: toggle,
    onClose: onCloseMenu,
  } = useDisclosure();

  const computeMenuPosition = useCallback((): MenuPosition | null => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    return { top: rect.bottom + MENU_VERTICAL_GAP, left: rect.left };
  }, []);

  const onToggleMenu = useCallback(() => {
    if (!isMenuOpen) {
      const position = computeMenuPosition();
      setMenuPosition(position);
    }
    toggle();
  }, [isMenuOpen, computeMenuPosition, toggle]);

  useClickOutside(menuRef, onCloseMenu, {
    enabled: isMenuOpen,
    excludeRefs: [portalMenuRef],
  });

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const reposition = () => {
      const position = computeMenuPosition();
      setMenuPosition(position);
    };

    window.addEventListener(WindowEventType.SCROLL, reposition, true);
    window.addEventListener(WindowEventType.RESIZE, reposition);

    return () => {
      window.removeEventListener(WindowEventType.SCROLL, reposition, true);
      window.removeEventListener(WindowEventType.RESIZE, reposition);
    };
  }, [isMenuOpen, computeMenuPosition]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KeyboardKey.ESCAPE) {
        onCloseMenu();
      }
    };

    document.addEventListener(KeyboardEventType.KEYDOWN, handleKeyDown);
    return () =>
      document.removeEventListener(KeyboardEventType.KEYDOWN, handleKeyDown);
  }, [isMenuOpen, onCloseMenu]);

  const menuItems: ComposeMenuItem[] = useMemo(
    () => [
      {
        id: 'create-dobox',
        labelKey: 'components.composeButton.createDoBox',
        icon: LuPlus,
        onClick: () => {
          onCloseMenu();
          onCreateTask();
        },
      },
    ],
    [onCloseMenu, onCreateTask],
  );

  return {
    menuRef,
    triggerRef,
    portalMenuRef,
    isMenuOpen,
    menuPosition,
    onToggleMenu,
    menuItems,
  };
}
